import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { inr, num } from "@/lib/api";
import { PageHeader, Panel, Loading, Pill } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  SlidersHorizontal, Download, X, ArrowUpDown, ArrowUp, ArrowDown, Columns3, Filter,
  FileBarChart2,
} from "lucide-react";

const EMPTY = {
  from_date: "", to_date: "", opco_id: "", region_id: "", rso_id: "", ao_id: "",
  dealer_id: "", scheme_id: "", engine: "", status: "",
};
const STATUS_OPTS = ["approved", "pending", "settled", "hold", "open", "investigating", "resolved"];

export default function Reports() {
  const [report, setReport] = useState("dealer_incentive");
  const [filters, setFilters] = useState(EMPTY);
  const [hidden, setHidden] = useState({});
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [colOpen, setColOpen] = useState(false);
  const colRef = useRef(null);

  const { data: catalog } = useQuery({ queryKey: ["rep-catalog"], queryFn: async () => (await api.get("/reports/catalog")).data });
  const { data: opts } = useQuery({ queryKey: ["rep-opts"], queryFn: async () => (await api.get("/reports/filter-options")).data });
  const { data, isFetching, isError } = useQuery({
    queryKey: ["rep-run", report, filters],
    queryFn: async () => (await api.post("/reports/run", { report, filters })).data,
    placeholderData: (prev) => prev,
  });

  useEffect(() => { setHidden({}); setSort({ key: null, dir: "asc" }); }, [report]);
  useEffect(() => {
    const onClick = (e) => { if (colRef.current && !colRef.current.contains(e.target)) setColOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const setOpco = (v) => set({ opco_id: v, region_id: "", rso_id: "", ao_id: "", dealer_id: "", scheme_id: "" });
  const setRegion = (v) => set({ region_id: v, rso_id: "", ao_id: "", dealer_id: "" });
  const setRso = (v) => set({ rso_id: v, ao_id: "", dealer_id: "" });
  const setAo = (v) => set({ ao_id: v, dealer_id: "" });

  const regions = useMemo(() => (opts?.regions || []).filter((r) => !filters.opco_id || r.opco_id === filters.opco_id), [opts, filters.opco_id]);
  const rsos = useMemo(() => (opts?.rsos || []).filter((r) => (!filters.opco_id || r.opco_id === filters.opco_id) && (!filters.region_id || r.region_id === filters.region_id)), [opts, filters.opco_id, filters.region_id]);
  const aos = useMemo(() => (opts?.aos || []).filter((a) => (!filters.region_id || a.region_id === filters.region_id) && (!filters.rso_id || a.rso_id === filters.rso_id) && (!filters.opco_id || a.opco_id === filters.opco_id)), [opts, filters]);
  const dealers = useMemo(() => (opts?.dealers || []).filter((d) => (!filters.opco_id || d.opco_id === filters.opco_id) && (!filters.region_id || d.region_id === filters.region_id) && (!filters.rso_id || d.rso_id === filters.rso_id) && (!filters.ao_id || d.ao_id === filters.ao_id)), [opts, filters]);
  const schemes = useMemo(() => (opts?.schemes || []).filter((s) => !filters.opco_id || s.opco_id === filters.opco_id), [opts, filters.opco_id]);

  const columns = data?.columns || [];
  const visibleCols = columns.filter((c) => !hidden[c.key]);

  const sortedRows = useMemo(() => {
    const rows = data?.rows || [];
    if (!sort.key) return rows;
    const col = columns.find((c) => c.key === sort.key);
    const numeric = col && (col.type === "number" || col.type === "currency");
    return [...rows].sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      const cmp = numeric ? (av || 0) - (bv || 0) : String(av ?? "").localeCompare(String(bv ?? ""));
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [data, sort, columns]);

  const toggleSort = (key) => setSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  const activeFilterCount = Object.entries(filters).filter(([k, v]) => v).length;

  const fmt = (c, v) => {
    if (v == null || v === "") return "—";
    if (c.type === "currency") return inr(v);
    if (c.type === "number") return num(v);
    return String(v);
  };

  const exportCSV = () => {
    const header = visibleCols.map((c) => c.label);
    const lines = [header.join(",")];
    for (const r of sortedRows) {
      lines.push(visibleCols.map((c) => {
        const v = r[c.key] ?? "";
        const s = String(v).replace(/"/g, '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
      }).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${report}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const selCls = "h-9 rounded-md border border-slate-200 px-2.5 bg-white text-sm text-slate-700 w-full";

  return (
    <div>
      <PageHeader title="Reports" subtitle="Detailed, filterable reports with column selection and CSV export"
        actions={
          <Button data-testid="report-export-btn" onClick={exportCSV} disabled={!sortedRows.length}
            className="gap-2 bg-[#0F2C59] hover:bg-[#00529B]">
            <Download size={16} /> Export CSV
          </Button>
        } />

      {/* Report type tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4" data-testid="report-tabs">
        {(catalog?.reports || []).map((r) => (
          <button key={r.id} data-testid={`report-tab-${r.id}`} onClick={() => setReport(r.id)}
            className={`px-3.5 py-2 rounded-lg text-sm font-medium whitespace-nowrap border flex items-center gap-2 ${report === r.id ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
            <FileBarChart2 size={15} /> {r.label}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-4" data-testid="report-filters">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <Filter size={15} /> Filters {activeFilterCount > 0 && <span className="text-xs text-[#00529B]">({activeFilterCount} active)</span>}
          </div>
          {activeFilterCount > 0 && (
            <Button variant="ghost" size="sm" className="gap-1 text-slate-500" data-testid="report-clear-filters" onClick={() => setFilters(EMPTY)}>
              <X size={14} /> Clear all
            </Button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
          <Field label="From Date"><Input type="date" data-testid="report-from-date" className="h-9" value={filters.from_date} onChange={(e) => set({ from_date: e.target.value })} /></Field>
          <Field label="To Date"><Input type="date" data-testid="report-to-date" className="h-9" value={filters.to_date} onChange={(e) => set({ to_date: e.target.value })} /></Field>
          <Field label="Company">
            <select className={selCls} disabled value="adani"><option value="adani">{opts?.company || "Adani Cement"}</option></select>
          </Field>
          <Field label="OpCo">
            <select className={selCls} data-testid="report-filter-opco" value={filters.opco_id} onChange={(e) => setOpco(e.target.value)}>
              <option value="">All OpCos</option>
              {(opts?.opcos || []).map((o) => <option key={o.id} value={o.id}>{o.code}</option>)}
            </select>
          </Field>
          <Field label="Region">
            <select className={selCls} data-testid="report-filter-region" value={filters.region_id} onChange={(e) => setRegion(e.target.value)}>
              <option value="">All Regions</option>
              {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </Field>
          <Field label="RSO">
            <select className={selCls} data-testid="report-filter-rso" value={filters.rso_id} onChange={(e) => setRso(e.target.value)}>
              <option value="">All RSOs</option>
              {rsos.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </Field>
          <Field label="AO">
            <select className={selCls} data-testid="report-filter-ao" value={filters.ao_id} onChange={(e) => setAo(e.target.value)}>
              <option value="">All AOs</option>
              {aos.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </Field>
          <Field label="Dealer">
            <select className={selCls} data-testid="report-filter-dealer" value={filters.dealer_id} onChange={(e) => set({ dealer_id: e.target.value })}>
              <option value="">All Dealers</option>
              {dealers.slice(0, 300).map((d) => <option key={d.id} value={d.id}>{d.code} — {d.name}</option>)}
            </select>
          </Field>
          <Field label="Scheme">
            <select className={selCls} data-testid="report-filter-scheme" value={filters.scheme_id} onChange={(e) => set({ scheme_id: e.target.value })}>
              <option value="">All Schemes</option>
              {schemes.map((s) => <option key={s.id} value={s.id}>{s.code}</option>)}
            </select>
          </Field>
          <Field label="Engine">
            <select className={selCls} data-testid="report-filter-engine" value={filters.engine} onChange={(e) => set({ engine: e.target.value })}>
              <option value="">All Engines</option>
              {(opts?.engines || []).map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select className={selCls} data-testid="report-filter-status" value={filters.status} onChange={(e) => set({ status: e.target.value })}>
              <option value="">All Status</option>
              {STATUS_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        </div>
      </div>

      {/* Results */}
      <Panel testid="report-results" title={data?.label || "Report"}
        subtitle={data ? `${data.count} rows${isFetching ? " · updating…" : ""}` : ""}
        actions={
          <div className="relative" ref={colRef}>
            <Button variant="outline" size="sm" className="gap-2" data-testid="report-columns-btn" onClick={() => setColOpen((o) => !o)}>
              <Columns3 size={15} /> Columns
            </Button>
            {colOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-lg shadow-lg z-30 p-2 max-h-72 overflow-y-auto" data-testid="report-columns-menu">
                <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold px-2 py-1">Toggle columns</div>
                {columns.map((c) => (
                  <label key={c.key} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 text-sm cursor-pointer">
                    <input type="checkbox" data-testid={`col-toggle-${c.key}`} checked={!hidden[c.key]}
                      onChange={() => setHidden((h) => ({ ...h, [c.key]: !h[c.key] }))} className="accent-[#00529B]" />
                    {c.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        }>
        {!data ? (
          isError ? <div className="text-center py-16 text-slate-400">Could not load this report. Your session may have expired.</div>
                  : <Loading label="Running report…" />
        ) : sortedRows.length === 0 ? (
          <div className="text-center py-16 text-slate-400">No records match your filters.</div>
        ) : (
          <div className="overflow-x-auto max-h-[65vh]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="text-left text-slate-400 text-xs uppercase tracking-wider border-b border-slate-200">
                  {visibleCols.map((c) => (
                    <th key={c.key} data-testid={`report-th-${c.key}`} onClick={() => toggleSort(c.key)}
                      className={`py-2.5 px-3 whitespace-nowrap cursor-pointer select-none hover:text-slate-600 ${c.type === "currency" || c.type === "number" ? "text-right" : ""}`}>
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        {sort.key === c.key ? (sort.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={11} className="opacity-30" />}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r, i) => (
                  <tr key={i} className="drill-row border-b border-slate-50" data-testid={`report-row-${i}`}>
                    {visibleCols.map((c) => (
                      <td key={c.key} className={`py-2.5 px-3 whitespace-nowrap ${c.type === "currency" || c.type === "number" ? "text-right tabular-nums" : "text-slate-700"} ${c.key === "final_amount" || c.key === "total_accrued" || c.key === "net" ? "font-semibold text-[#00529B]" : ""}`}>
                        {c.key === "status" || c.key === "severity" || c.key === "engine" || c.key === "erp_sync"
                          ? <Pill tone={r[c.key]}>{String(r[c.key] ?? "").replace("_", " ")}</Pill>
                          : fmt(c, r[c.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {Object.keys(data.totals || {}).length > 0 && (
                <tfoot className="sticky bottom-0 bg-slate-50">
                  <tr className="border-t-2 border-slate-200 font-semibold" data-testid="report-totals-row">
                    {visibleCols.map((c, idx) => (
                      <td key={c.key} className={`py-2.5 px-3 whitespace-nowrap ${c.type === "currency" || c.type === "number" ? "text-right tabular-nums text-[#0F2C59]" : "text-slate-500"}`}>
                        {idx === 0 ? "Totals" : (data.totals[c.key] != null ? fmt(c, data.totals[c.key]) : "")}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">{label}</div>
      {children}
    </div>
  );
}
